import React, { useState } from 'react';
import { View, TouchableOpacity, ActivityIndicator, NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faDownload, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import MyDataTable from '../../../components/MyDataTable';
import MyAppText from '../../../components/MyAppText';
import Toast from 'react-native-toast-message';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { DeacParamList } from '../../../typings/Params';
import ReportModal from './ReportModal';
import { useStyles } from 'react-native-unistyles';
import FilterInput from '../../../components/Filter/components/FilterInput';
import CustomButton from '../../../components/CustomButton';
import { deacService } from '../../../services/central-api/deac';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { useAuth } from '../../../context/Auth';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';
import { RootNavigation } from '../../../Routes';
import { openExternalLinkInNewTab } from '../../../services/navigation-assistant';

export default function Reports({ navigation }: { navigation: RootNavigation; }) {
    const { theme } = useStyles();
    const route = useRoute<RouteProp<DeacParamList, 'DeacReports'>>();
    const { user: authenticatedUser } = useAuth();

    // filters
    const [filters, setFilters] = useFilters<DeacParamList['DeacReports']>({
        page: Math.max(+route.params.page - 1, 0),
        textFilter: route.params.textFilter ?? '',
        limit: Number(route.params.limit) || 25,
        actingBodyId: getActingBodyId(),
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [isDownloading, setIsDownloading] = useState(false);
    const [isModalVisible, setModalVisible] = useState<boolean>(false);


    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['deacReports', filters],
        queryFn: () => deacService.getReports(filters),
        enabled: !!authenticatedUser && isFocused,
        refetchInterval: isFocused ? 15000 : false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    function handleFilters() {
        setFilters({
            ...filters,
            page: 0,
            textFilter: textFilter != '' ? textFilter : undefined,
        });

        navigation.setParams({
            page: 1,
            textFilter: textFilter != '' ? textFilter : undefined,
        });
    }

    function handleFormClose() {
        setModalVisible(false);
        refetch();
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            handleFilters();
        }
    }

    function getActingBodyId() {
        if (!authenticatedUser || !authenticatedUser.actingBodyUnit.actingBodyId) {
            return 0;
        }

        if (authenticatedUser.isAdmin || authenticatedUser.permissions.deac_manage_all_acting_bodies_reports) {
            return undefined;
        } else {
            return authenticatedUser.actingBodyUnit.actingBodyId;
        }
    }

    return (
        <Container>
            <Header
                title={translate('reports')}
                actions={<CustomButton
                    text={translate('createReport')}
                    onPress={() => setModalVisible(true)}
                />}
            />
            <Card>
                <DataTableHeader
                    title={translate('createdReports')}
                    zIndex={2}
                    filterInput={<FilterInput
                        value={textFilter}
                        placeholder={translate('typeToSearch')}
                        onChange={setTextFilter}
                        onKeyPress={handleKeyDown}
                    />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[
                            {
                                name: translate('name'),
                                selector: row => row.name,
                                wrap: true
                            },
                            {
                                name: translate('user'),
                                selector: row => row.user?.name || '',
                                wrap: true
                            },
                            {
                                name: 'Token',
                                selector: row => row.token,
                                wrap: true
                            },
                            {
                                name: translate('status'),
                                cell: row =>
                                    <View style={{ flexDirection: 'row', columnGap: 5, alignItems: 'center', width: '100%' }}>
                                        {row.status == 'pending' ?
                                            <ActivityIndicator size={16} color={theme.colors.iconColor} /> :
                                            <FontAwesomeIcon style={row.status == 'success' ? { color: '#017B27' } : { color: '#B40119' }} fontSize={16} icon={row.status == 'success' ? faCheckCircle : faTimesCircle}></FontAwesomeIcon>
                                        }

                                        <MyAppText style={row.status == 'success' ? { color: '#017B27' } : row.status == 'failed' ? { color: '#B40119' } : {}}>{translate(row.status)}</MyAppText>
                                    </View>
                            },
                            {
                                name: translate('filters'),
                                wrap: true,
                                cell: row => <View style={{ width: '100%' }}>
                                    {row.type == 'deac_vacancies' ?
                                        <View style={{ flexDirection: 'column', rowGap: 5 }}>
                                            <MyAppText>{translate('start')}: {moment(row.filters.startDate).format('DD/MM/YYYY HH:mm')}</MyAppText>
                                            <MyAppText>{translate('end')}: {moment(row.filters.endDate).format('DD/MM/YYYY  HH:mm')}</MyAppText>
                                            {row.filters.user && row.filters.user != '' ? <MyAppText>{translate('agent')}: {row.filters.user}</MyAppText> : undefined}
                                            {row.filters.workplaceId ? <MyAppText>{translate('workplace')}: {row.filters.workplaceName}</MyAppText> : undefined}
                                            {row.filters.actingBodyCommandsIds ? <MyAppText>{translate('command')}: {row.filters.actingBodyCommands}</MyAppText> : undefined}
                                            {row.filters.actingBodyUnitsIds ? <MyAppText>{translate('unit')}: {row.filters.actingBodyUnits}</MyAppText> : undefined}
                                        </View> : <></>}
                                </View>
                            },
                            {
                                name: translate('createdAt'),
                                selector: row => moment(row.createdAt).format('DD/MM/YYYY HH:mm:ss'),
                            },
                            {
                                name: translate('action'),
                                button: true,
                                cell: row =>
                                    <TouchableOpacity disabled={isDownloading} onPress={async () => {
                                        try {
                                            setIsDownloading(true);
                                            const video = await deacService.getReport(row.id);
                                            openExternalLinkInNewTab(video.resultUrl);
                                        } catch (error) {
                                            Toast.show({
                                                type: 'sentinelxError',
                                                text1: translate('unexpectedError'),
                                            });
                                        } finally {
                                            setIsDownloading(false);
                                        }
                                    }}>
                                        {row.status === 'success' ? <FontAwesomeIcon fontSize={16} color={theme.colors.iconColor} icon={faDownload}></FontAwesomeIcon> : null}
                                    </TouchableOpacity>
                            }
                        ]}
                        data={data?.rows || []}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        totalItems={data?.count || 0}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />
            </Card>

            <ReportModal
                isModalVisible={isModalVisible}
                setModalVisible={setModalVisible}
                onClose={handleFormClose}
            />
        </Container>
    );
}
